import { useEffect, useState, useRef } from "react";
import { api } from "@/lib/api";
import useChainSession from "@/hooks/useChainSession";
import { RefreshCw, Wifi, WifiOff, Clock } from "lucide-react";

function fmt(sec) {
  const m = Math.floor(sec / 60);
  const s = sec % 60;
  return `${m}:${String(s).padStart(2, "0")}`;
}

export default function ChainStatus() {
  const [chainData, setChainData] = useState(null);
  const [fetchError, setFetchError] = useState(false);
  const { active, secondsRemaining, ttl, expired, refresh } = useChainSession();

  // Local countdown that ticks every second between server polls
  const [localSec, setLocalSec] = useState(secondsRemaining);
  const tickRef = useRef(null);

  // Sync local countdown whenever the server gives us a fresh value
  useEffect(() => {
    setLocalSec(secondsRemaining);
  }, [secondsRemaining]);

  // Tick down every second
  useEffect(() => {
    if (tickRef.current) clearInterval(tickRef.current);
    if (active && localSec > 0) {
      tickRef.current = setInterval(() => {
        setLocalSec((s) => Math.max(0, s - 1));
      }, 1000);
    }
    return () => clearInterval(tickRef.current);
  }, [active, localSec > 0]); // eslint-disable-line react-hooks/exhaustive-deps

  // Fetch chain status (separate from session — just for connected/block)
  useEffect(() => {
    if (!active) return;
    const load = async () => {
      try {
        const { data } = await api.get("/api/wallet/chain/status");
        setChainData(data);
        setFetchError(false);
      } catch {
        setFetchError(true);
      }
    };
    load();
    const iv = setInterval(load, 15000);
    return () => clearInterval(iv);
  }, [active]);

  const pct = ttl > 0 ? Math.round((localSec / ttl) * 100) : 0;
  const isLow = localSec < 60;

  // ── Expired ────────────────────────────────────────────────────────────────
  if (expired || (!active && localSec === 0)) {
    return (
      <div className="mt-4 flex flex-col gap-2 max-w-xs">
        <div className="flex items-center gap-2 text-[12px] font-medium text-amber-700 bg-amber-50 border border-amber-200 px-3 py-2 rounded-xl">
          <Clock size={13} className="shrink-0" />
          <span>Live feed expired after 5 min</span>
        </div>
        <button
          onClick={refresh}
          className="flex items-center gap-1.5 text-[12px] font-semibold text-white bg-[#022dee] hover:bg-[#0122bb] px-3 py-1.5 rounded-lg w-max transition-colors"
        >
          <RefreshCw size={12} />
          Re-enable live feed
        </button>
      </div>
    );
  }

  // ── Not yet active / loading ───────────────────────────────────────────────
  if (!active) {
    return (
      <div className="flex items-center gap-2 text-[12px] font-medium text-slate-400 mt-4">
        <div className="w-2 h-2 rounded-full bg-slate-300 animate-pulse" />
        Connecting to BSC…
      </div>
    );
  }

  // ── RPC error ─────────────────────────────────────────────────────────────
  if (fetchError) {
    return (
      <div className="flex items-center gap-2 text-[12px] font-medium text-red-500 bg-red-50 px-3 py-1.5 rounded-full w-max mt-4">
        <WifiOff size={12} />
        Chain status unavailable
      </div>
    );
  }

  // ── Active session ─────────────────────────────────────────────────────────
  const connected = chainData?.connected;
  const blockNum = chainData?.block_number ?? 0;
  const network = chainData?.network ?? "BSC";

  return (
    <div className="mt-4 flex flex-col gap-2 max-w-xs">
      {/* Connection badge */}
      {connected ? (
        <div className="flex items-center gap-2 text-[12px] font-medium text-emerald-600 bg-emerald-50 px-3 py-1.5 rounded-full w-max">
          <Wifi size={12} />
          {network} · Block #{blockNum.toLocaleString()}
        </div>
      ) : (
        <div className="flex items-center gap-2 text-[12px] font-medium text-red-500 bg-red-50 px-3 py-1.5 rounded-full w-max">
          <WifiOff size={12} />
          BSC Offline{chainData?.error ? ` · ${chainData.error}` : ""}
        </div>
      )}

      {/* Session timer */}
      <div className="flex flex-col gap-1">
        <div className="flex items-center justify-between text-[11px] text-slate-500">
          <span className="flex items-center gap-1">
            <Clock size={10} />
            Live feed
          </span>
          <span className={isLow ? "text-amber-600 font-semibold" : ""}>
            {fmt(localSec)} remaining
          </span>
        </div>

        {/* Progress bar */}
        <div className="h-1.5 w-full bg-slate-100 rounded-full overflow-hidden">
          <div
            className={`h-full rounded-full transition-all duration-1000 ${
              isLow ? "bg-amber-400" : "bg-[#022dee]"
            }`}
            style={{ width: `${pct}%` }}
          />
        </div>

        {isLow && (
          <p className="text-[10px] text-amber-600">
            Feed expires soon —{" "}
            <button
              onClick={refresh}
              className="underline font-semibold hover:text-amber-700"
            >
              extend
            </button>
          </p>
        )}
      </div>
    </div>
  );
}
